import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Grid as FixedSizeGrid } from "react-window";
import { AutoSizer } from "react-virtualized-auto-sizer";
import Pagination from "@mui/material/Pagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress,
  MenuItem,
  Chip,
  Select,
  InputLabel,
  FormControl,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { AppContext } from "../App";
import { deleteUser, updateUser } from "../graphql/mutations";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import UserRow from "./UserRow";
import PricelistSelect from "./pricing/PricelistSelect";
import SuperAdminUserCard from "./shared/SuperAdminUserCard";
import {
  DeleteConfirmationDialog,
  UserDialog,
  CreateUserDialog,
} from "./shared";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// Helper function to find duplicate emails (case insensitive)
const findDuplicateEmails = (users) => {
  const emailCount = {};
  users.forEach((user) => {
    const normalizedEmail = user.email?.toLowerCase().trim();
    if (normalizedEmail) {
      emailCount[normalizedEmail] = (emailCount[normalizedEmail] || 0) + 1;
    }
  });
  return emailCount;
};

// Helper function to count customers per provider
const getProviderCustomerCounts = (users) => {
  const providerCount = {};

  users.forEach((user) => {
    // Only count users that have a provider (createdByUserId is set and not 'deleted')
    // and are not providers themselves (regular customers)
    const providerId = user.createdByUserId;
    const isRegularUser =
      user.isProvider === false ||
      user.isProvider === null ||
      user.isProvider === undefined;

    if (providerId && providerId !== "deleted" && isRegularUser) {
      providerCount[providerId] = (providerCount[providerId] || 0) + 1;
    }
  });

  return providerCount;
};

// Main SuperAdminUsers Component
const SuperAdminUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("recordOnly");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'row'
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const theme = useTheme();
  const { userRecord } = useContext(AppContext);

  // Pagination settings - optimized for performance
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const pageSizes = [50, 100, 200, 300];
  const totalPages = Math.ceil(allUsers.length / pageSize);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("DESC");

  // State for fetched pricelist names (cached for session)
  const [fetchedPricelists, setFetchedPricelists] = useState({});
  const [loadingPricelists, setLoadingPricelists] = useState({});

  // Responsive columns for virtual scrolling
  const [columns, setColumns] = useState(3);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 600) setColumns(1);
      else if (width < 960) setColumns(2);
      else if (width < 1200) setColumns(3);
      else setColumns(4);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  // Optimized GraphQL query - fetch only scalar IDs, not nested relations
  const listUsersCustom = `
    query ListUsers(
      $limit: Int,
      $nextToken: String
    ) {
      listUsers(limit: $limit, nextToken: $nextToken) {
        items {
          id
          username
          tier
          email
          activePricelistID
          searchesRemaining
          subscriptionActive
          subscriptionTier
          subscriptionTerm
          isProvider
          secondPricelistID
          companyLogo
          priceOffsetType
          priceOffsetValue
          providerOffsetType
          providerOffsetValue
          createdAt
          lastLogin
          createdByUserId
        }
        nextToken
      }
    }
  `;

  // Query for fetching pricelist on-demand
  const getPricelistQuery = `
    query GetPricelist($id: ID!) {
      getPricelist(id: $id) {
        id
        name
      }
    }
  `;

  // Fetch pricelist name on-demand
  const fetchPricelistName = useCallback(async (pricelistId) => {
    if (!pricelistId) return null;
    if (fetchedPricelists[pricelistId]) return fetchedPricelists[pricelistId];

    setLoadingPricelists((prev) => ({ ...prev, [pricelistId]: true }));
    try {
      const response = await API.graphql(
        graphqlOperation(getPricelistQuery, { id: pricelistId }),
      );
      const pricelistName = response.data.getPricelist?.name || null;
      setFetchedPricelists((prev) => ({ ...prev, [pricelistId]: pricelistName }));
      return pricelistName;
    } catch (error) {
      console.error(`Error fetching pricelist ${pricelistId}:`, error);
      return null;
    } finally {
      setLoadingPricelists((prev) => ({ ...prev, [pricelistId]: false }));
    }
  }, [fetchedPricelists, getPricelistQuery]);

  // Track pagination state
  const [nextToken, setNextToken] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Use the pageSize state variable instead of hardcoded limits
  const initialFetchLimit = pageSize;

  const fetchUsers = useCallback(
    async (limit = 300, token = null, reset = true, autoLoadAll = false) => {
      if (!userRecord) return { items: [], nextToken: null };

      try {
        if (reset) {
          setLoading(true);
          setAllUsers([]);
          setTotalUsers(0);
          setNextToken(null);
        }

        const response = await API.graphql(
          graphqlOperation(listUsersCustom, { limit, nextToken: token }),
        );

        let fetchedUsers = response.data.listUsers.items || [];
        const newNextToken = response.data.listUsers.nextToken || null;

        setAllUsers((prev) => {
          const existingIds = new Set(prev.map((u) => u.id));
          const uniqueUsers = fetchedUsers.filter((u) => !existingIds.has(u.id));
          const newUsers = [...prev, ...uniqueUsers];
          console.log(
            `[SuperAdminUsers] Fetched ${fetchedUsers.length} users, added ${uniqueUsers.length} unique users, total: ${newUsers.length}`,
          );
          setTotalUsers(newUsers.length);
          return newUsers;
        });

        setNextToken(newNextToken);

        if (autoLoadAll && newNextToken) {
          await fetchUsers(limit, newNextToken, false, autoLoadAll);
        }

        if (reset) {
          setLoading(false);
        }

        return { success: true, items: fetchedUsers, nextToken: newNextToken };
      } catch (error) {
        console.error("Error fetching users:", error);
        if (reset) {
          setLoading(false);
        }
        return { success: false, items: [], nextToken: null };
      }
    },
    [userRecord, listUsersCustom],
  );

  // Load more users on demand
  const loadMoreUsers = useCallback(() => {
    if (!nextToken || loading || isLoadingMore) return;

    setIsLoadingMore(true);
    fetchUsers(pageSize, nextToken, false)
      .then(({ success }) => {
        if (success && nextToken) {
          setCurrentPage(currentPage + 1);
        }
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [nextToken, loading, isLoadingMore, fetchUsers, currentPage, pageSize]);

  // Sort the users client-side
  const getSortedUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      const valA = a[sortField] ?? "";
      const valB = b[sortField] ?? "";

      // Handle date fields
      if (sortField === "createdAt" && a.createdAt && b.createdAt) {
        return sortDirection === "ASC"
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      }

      // Handle string fields (email, username, etc.) with case-insensitive comparison
      if (typeof valA === "string" && typeof valB === "string") {
        return sortDirection === "ASC"
          ? valA.toLowerCase().localeCompare(valB.toLowerCase())
          : valB.toLowerCase().localeCompare(valA.toLowerCase());
      }

      return 0;
    });
  };

  const resetPagination = useCallback(() => {
    setAllUsers([]);
    setNextToken(null);
    setTotalUsers(0);
    setCurrentPage(1);
    fetchUsers(300, null, true, true);
  }, [fetchUsers]);

  useEffect(() => {
    resetPagination();
  }, [userRecord, resetPagination]);

  useEffect(() => {
    // Apply sorting when sort field or direction changes
    const sorted = getSortedUsers(allUsers);

    // Count deleted users for debugging
    const deletedCount = sorted.filter((user) => user.createdByUserId === "deleted").length;
    console.log("[SuperAdminUsers] Filtering:", {
      totalUsers: sorted.length,
      deletedCount,
      showDeleted,
      search: debouncedSearch,
    });

    // Then apply filtering with debounced search
    const filtered = sorted.filter((user) => {
      // Filter by deleted status based on toggle
      if (!showDeleted && user.createdByUserId === "deleted") {
        return false;
      }
      // Search filter with debounce
      return Object.values(user).some((value) =>
        value?.toString().toLowerCase().includes(debouncedSearch.toLowerCase()),
      );
    });

    // Apply pagination - only paginate displayed results, not all users
    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + pageSize);

    console.log("[SuperAdminUsers] After filtering:", {
      filteredCount: filtered.length,
      displayedCount: paginated.length,
    });

    setFilteredUsers(filtered);
    setDisplayedUsers(paginated);
  }, [
    debouncedSearch,
    allUsers,
    showDeleted,
    sortField,
    sortDirection,
    currentPage,
    pageSize,
  ]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditUser = (updatedUser) => {
    setAllUsers(
      allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
    );
    setDisplayedUsers(
      displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
    );
    setSelectedUser(updatedUser);
  };

  const handlePricelistUpdate = async (updatedUser) => {
    console.log("[SuperAdminUsers] handlePricelistUpdate:", updatedUser);
    try {
      setAllUsers(
        allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
      setDisplayedUsers(
        displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
    } catch (error) {
      console.error("Error updating pricelist:", error);
    }
  };

  const handleProviderToggle = async (user) => {
    console.log("═".repeat(80));
    console.log("[SuperAdminUsers] === handleProviderToggle START ===");
    console.log("[SuperAdminUsers] User passed to handler:", {
      id: user.id,
      username: user.username,
      email: user.email,
      isProvider: user.isProvider,
    });
    console.log("═".repeat(80));

    // Store previous state for rollback (outside try block so it's accessible in catch)
    const previousAllUsers = [...allUsers];
    const previousDisplayedUsers = [...displayedUsers];

    try {
      const newIsProvider = !user.isProvider;
      console.log("[SuperAdminUsers] Current isProvider:", user.isProvider, "-> New isProvider:", newIsProvider);
      const updatedUser = { ...user, isProvider: newIsProvider };
      
      console.log("[SuperAdminUsers] New isProvider value:", updatedUser.isProvider);
      
      // Update local state (optimistic update)
      console.log("[SuperAdminUsers] Applying optimistic update...");
      console.log("[SuperAdminUsers] allUsers before update (finding user):", 
        allUsers.find(u => u.id === updatedUser.id));
      const newAllUsers = allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u));
      const newDisplayedUsers = displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u));
      console.log("[SuperAdminUsers] allUsers after update (finding user):", 
        newAllUsers.find(u => u.id === updatedUser.id));
      setAllUsers(newAllUsers);
      setDisplayedUsers(newDisplayedUsers);
      
      // Persist to database
      console.log("[SuperAdminUsers] Calling GraphQL mutation...");
      const response = await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: updatedUser.id,
            isProvider: updatedUser.isProvider,
          },
        }),
      );
      
      console.log("[SuperAdminUsers] GraphQL response received:", response);
      
      // Use actual response from server to update state
      const serverUser = response?.data?.updateUser;
      if (serverUser) {
        console.log("[SuperAdminUsers] Server response user:", {
          id: serverUser.id,
          username: serverUser.username,
          isProvider: serverUser.isProvider,
        });
        
        setAllUsers(
          allUsers.map((u) => (u.id === serverUser.id ? serverUser : u)),
        );
        setDisplayedUsers(
          displayedUsers.map((u) => (u.id === serverUser.id ? serverUser : u)),
        );
        
        console.log("[SuperAdminUsers] ✅ Successfully updated provider status");
        console.log("[SuperAdminUsers] User after update:", serverUser);
        
        // Show success notification
        setSnackbar({
          open: true,
          message: `Successfully updated ${serverUser.username}'s provider status to ${serverUser.isProvider ? "Provider" : "Customer"}`,
          severity: "success",
        });
      } else {
        console.warn("[SuperAdminUsers] ⚠️ No serverUser in response, reverting optimistic update");
        setAllUsers(previousAllUsers);
        setDisplayedUsers(previousDisplayedUsers);
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("═".repeat(80));
      console.error("[SuperAdminUsers] ❌ ERROR toggling provider status");
      console.error("[SuperAdminUsers] Error:", error);
      console.error("[SuperAdminUsers] Error message:", error.message);
      console.error("═".repeat(80));
      
      // Revert optimistic update
      console.log("[SuperAdminUsers] Reverting optimistic update...");
      setAllUsers(previousAllUsers);
      setDisplayedUsers(previousDisplayedUsers);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: `Failed to update provider status: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      console.log("═".repeat(80));
      console.log("[SuperAdminUsers] === handleProviderToggle END ===");
      console.log("═".repeat(80));
    }
  };

  const handleProviderChange = async (userId, providerId) => {
    console.log("[SuperAdminUsers] handleProviderChange:", userId, providerId);
    try {
      // Update local state
      setAllUsers(
        allUsers.map((u) =>
          u.id === userId ? { ...u, createdByUserId: providerId } : u,
        ),
      );
      setDisplayedUsers(
        displayedUsers.map((u) =>
          u.id === userId ? { ...u, createdByUserId: providerId } : u,
        ),
      );
      // Persist to database
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: userId,
            createdByUserId: providerId,
          },
        }),
      );
    } catch (error) {
      console.error("Error updating provider:", error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Reverse direction if same field clicked
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      // New field, default to ASC
      setSortField(field);
      setSortDirection("ASC");
    }
  };

  const handleViewEdit = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteType("recordOnly");
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (type) => {
    if (!selectedUser) return;

    try {
      if (type === "complete") {
        // Update user with deleted flag in DynamoDB
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: selectedUser.id,
              createdByUserId: "deleted",
            },
          }),
        );
      }

      // Delete the user record from DynamoDB
      await API.graphql(
        graphqlOperation(deleteUser, { input: { id: selectedUser.id } }),
      );

      // Update local state
      const updatedUsers = allUsers.filter((u) => u.id !== selectedUser.id);
      setAllUsers(updatedUsers);
      setDisplayedUsers(displayedUsers.filter((u) => u.id !== selectedUser.id));

      handleCloseDeleteDialog();
      setSnackbar({
        open: true,
        message: `User ${selectedUser.email} deleted successfully (${
          type === "complete" ? "complete" : "record only"
        })`,
        severity: "success",
      });
      console.log(
        `User ${
          type === "complete" ? "completely" : "record only"
        } deleted successfully`,
      );
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: `Error deleting user: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    }
  };

  // Calculate duplicate emails whenever users change
  const duplicateEmails = useMemo(
    () => findDuplicateEmails(allUsers),
    [allUsers],
  );

  // Calculate customer counts per provider
  const providerCustomerCount = useMemo(
    () => getProviderCustomerCounts(allUsers),
    [allUsers],
  );

  // Pre-compute provider list once (avoid O(n²) filtering in cards)
  const providerUsers = useMemo(
    () => allUsers.filter((u) => u.isProvider),
    [allUsers],
  );

  const isProcessing = useMemo(() => {
    // Check if processing is happening: sort, filter, or pagination is in progress
    return (
      filteredUsers.length === 0 &&
      displayedUsers.length === 0 &&
      allUsers.length > 0
    );
  }, [allUsers, filteredUsers, displayedUsers]);

  const showNoUsers = useMemo(() => {
    // Show "no users" only if really no users at all
    return !loading && displayedUsers.length === 0;
  }, [loading, displayedUsers]);

  const isInitialLoad = useMemo(() => {
    // Check if this is the initial load state
    return allUsers.length === 0 && loading;
  }, [allUsers, loading]);

  if (isInitialLoad || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading users...
        </Typography>
      </Box>
    );
  }

  // Debug logging
  console.log("[SuperAdminUsers] Rendering with:", {
    displayedUsersLength: displayedUsers.length,
    allUsersLength: allUsers.length,
    filteredUsersLength: filteredUsers.length,
    viewMode,
    columns,
  });

  return (
    <Box sx={{ margin: "20px" }}>
      <TextField
        fullWidth
        placeholder="Search users by name, email, or any field..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          "& .MuiOutlinedInput-root": {
            borderRadius: 2,
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {displayedUsers.length} of {allUsers.length} users
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* Show Deleted Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                color="secondary"
                size="small"
              />
            }
            label="Show Deleted"
            sx={{ fontSize: "0.875rem" }}
          />
          {/* Sort Dropdown */}
          <FormControl sx={{ minWidth: 140 }}>
            <Select
              value={`${sortField}-${sortDirection}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split("-");
                setSortField(field);
                setSortDirection(direction);
              }}
              size="small"
            >
              <MenuItem value="email-ASC">Sort A-Z (Email)</MenuItem>
              <MenuItem value="email-DESC">Sort Z-A (Email)</MenuItem>
              <MenuItem value="username-ASC">Sort A-Z (Name)</MenuItem>
              <MenuItem value="username-DESC">Sort Z-A (Name)</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton
              onClick={() => setViewMode("card")}
              color={viewMode === "card" ? "primary" : "default"}
              size="small"
              title="Card View"
              sx={{
                backgroundColor:
                  viewMode === "card"
                    ? "primary.light"
                    : "rgba(255,255,255,0.1)",
                "& svg": {
                  color:
                    viewMode === "card"
                      ? "primary.contrastText"
                      : "text.primary",
                },
              }}
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              onClick={() => setViewMode("row")}
              color={viewMode === "row" ? "primary" : "default"}
              size="small"
              title="Row View"
              sx={{
                backgroundColor:
                  viewMode === "row"
                    ? "primary.light"
                    : "rgba(255,255,255,0.1)",
                "& svg": {
                  color:
                    viewMode === "row"
                      ? "primary.contrastText"
                      : "text.primary",
                },
              }}
            >
              <TableRowsIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {displayedUsers.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography color="text.secondary">
            {search ? "No users match your search" : "No users found"}
          </Typography>
        </Box>
      ) : viewMode === "card" ? (
        <Box
          ref={containerRef}
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columns}, 1fr)`,
            gap: 2,
            padding: 1,
            maxWidth: "100%",
          }}
        >
          {displayedUsers.map((user) => (
            <Box
              key={user.id}
              sx={{
                width: "100%",
              }}
            >
              <SuperAdminUserCard
                user={user}
                onView={handleViewEdit}
                onEdit={handleViewEdit}
                onDelete={handleDeleteClick}
                isDuplicate={duplicateEmails[user.email?.toLowerCase().trim()] > 1}
                providerUsers={providerUsers}
                onProviderChange={handleProviderChange}
                onProviderToggle={handleProviderToggle}
                fetchedPricelists={fetchedPricelists}
                loadingPricelists={loadingPricelists}
                fetchPricelistName={fetchPricelistName}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 1200 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell
                  sx={{
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => handleSort("email")}
                >
                  Email{" "}
                  {sortField === "email" &&
                    (sortDirection === "ASC" ? "↑" : "↓")}
                </TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Pricelist</TableCell>
                <TableCell>Offset</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedUsers.map((user) => {
                const getRelativeTime = (dateString) => {
                  if (!dateString) return { text: "Never", status: "never" };

                  const date = new Date(dateString);
                  const now = new Date();
                  const diffInSeconds = Math.floor((now - date) / 1000);

                  if (diffInSeconds < 60) {
                    return { text: "Just now", status: "recent" };
                  }

                  const diffInMinutes = Math.floor(diffInSeconds / 60);
                  if (diffInMinutes < 60) {
                    return {
                      text: `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`,
                      status: diffInMinutes < 30 ? "recent" : "warning",
                    };
                  }

                  const diffInHours = Math.floor(diffInMinutes / 60);
                  if (diffInHours < 24) {
                    return {
                      text: `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`,
                      status: diffInHours < 12 ? "recent" : "warning",
                    };
                  }

                  const diffInDays = Math.floor(diffInHours / 24);
                  if (diffInDays < 7) {
                    return {
                      text: `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`,
                      status: diffInDays < 3 ? "warning" : "old",
                    };
                  }

                  const diffInWeeks = Math.floor(diffInDays / 7);
                  if (diffInWeeks < 4) {
                    return {
                      text: `${diffInWeeks} week${diffInWeeks > 1 ? "s" : ""} ago`,
                      status: "old",
                    };
                  }

                  const diffInMonths = Math.floor(diffInDays / 30);
                  if (diffInMonths < 12) {
                    return {
                      text: `${diffInMonths} month${diffInMonths > 1 ? "s" : ""} ago`,
                      status: "old",
                    };
                  }

                  return { text: date.toLocaleDateString(), status: "old" };
                };

                const lastLogin = getRelativeTime(user.lastLogin);
                // Determine offset display
                const offsetValue =
                  user.priceOffsetValue !== null &&
                  user.priceOffsetValue !== undefined
                    ? user.priceOffsetValue
                    : 0;
                const offsetType = user.priceOffsetType || "percentage";

                return (
                  <TableRow key={user.id}>
                    <TableCell>{user.username || "Unknown User"}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{lastLogin.text}</TableCell>
                    <TableCell>{user.userGroupID || "Unassigned"}</TableCell>
<TableCell>
                       <Box>
                         {user.activePricelistID && (
                           <Typography
                             variant="body2"
                             sx={{ mb: 0.5, fontWeight: "bold" }}
                           >
                             {fetchedPricelists[user.activePricelistID] || (
                               <Link
                                 component="button"
                                 onClick={() => fetchPricelistName(user.activePricelistID)}
                                 sx={{
                                   color: "primary.main",
                                   textDecoration: "none",
                                   "&:hover": { textDecoration: "underline" },
                                 }}
                               >
                                 View name
                               </Link>
                             )}
                           </Typography>
                         )}
                         <PricelistSelect
                           pricelistID={user.activePricelistID}
                           userID={user.id}
                           providerID={user.createdByUserId || userRecord?.id}
                          onPricelistUpdate={handlePricelistUpdate}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {/* Customer's Personal Offset */}
                        <Chip
                          label={
                            offsetValue !== 0
                              ? offsetType === "percentage"
                                ? `${offsetValue > 0 ? "+" : ""}${offsetValue}%`
                                : `$${offsetValue > 0 ? "+" : ""}${Math.abs(
                                    offsetValue,
                                  ).toFixed(2)}`
                              : "No offset"
                          }
                          size="small"
                          color={
                            offsetValue > 0
                              ? "warning"
                              : offsetValue < 0
                                ? "success"
                                : "default"
                          }
                          sx={{ fontWeight: "bold", width: "fit-content" }}
                        />
                        {/* Provider's Personal Offset */}
                        <Chip
                          label={
                            user.providerOffsetValue &&
                            user.providerOffsetValue !== 0
                              ? user.providerOffsetType === "percentage"
                                ? `${user.providerOffsetValue > 0 ? "+" : ""}${user.providerOffsetValue}%`
                                : `$${user.providerOffsetValue > 0 ? "+" : ""}${Math.abs(
                                    user.providerOffsetValue,
                                  ).toFixed(2)}`
                              : "No offset"
                          }
                          size="small"
                          color={
                            user.providerOffsetValue &&
                            user.providerOffsetValue > 0
                              ? "warning"
                              : user.providerOffsetValue &&
                                  user.providerOffsetValue < 0
                                ? "success"
                                : "default"
                          }
                          sx={{ fontWeight: "bold", width: "fit-content" }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewEdit(user)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UserDialog
        open={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        onEditUser={handleEditUser}
        isSuperAdmin={true}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => handleDeleteUser(deleteType)}
        user={selectedUser}
        deleteType={deleteType}
      />

      <CreateUserDialog
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 3,
          mb: 2,
          gap: 2,
        }}
      >
        <TextField
          select
          size="small"
          value={pageSize}
          onChange={handlePageSizeChange}
          label="Rows per page"
          sx={{ minWidth: 120 }}
        >
          {pageSizes.map((size) => (
            <MenuItem key={size} value={size}>
              {size} per page
            </MenuItem>
          ))}
        </TextField>

        <Pagination
          count={Math.ceil(filteredUsers.length / pageSize)}
          page={currentPage}
          onChange={handlePageChange}
          boundaryCount={1}
          siblingCount={1}
          showFirstButton
          showLastButton
          disabled={loading}
          color="primary"
        />

        {nextToken && (
          <Button
            variant="outlined"
            onClick={loadMoreUsers}
            startIcon={<RefreshIcon />}
            disabled={loading || isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </Button>
        )}
      </Box>

      <Fab
        color="primary"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
        }}
        onClick={() => setCreateUserModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminUsers;
